<template>
  <div
    class="group relative cursor-pointer select-none text-center"
    :class="[
      isActive ? 'z-10' : 'z-1',
      isInsideModal
        ? '-ml-px -mt-px grid aspect-square place-items-center hover:z-20'
        : 'flex flex-col'
    ]"
    :title="option.name"
    :aria-label="isSeeMore ? seeMoreLabel : optionName"
    :aria-checked="isSeeMore ? undefined : isActive"
    :role="isSeeMore ? 'button' : 'radio'"
    :tabindex="isSeeMore || isActive || isFocusable ? 0 : -1"
    @click="select"
    @gallery-radio-select="selectWithKeyboard"
    @keydown.enter.prevent="select"
    @keydown.space.prevent="select"
    @keydown.left.prevent="moveRadioFocus(-1, $event)"
    @keydown.up.prevent="moveRadioFocus(-1, $event)"
    @keydown.right.prevent="moveRadioFocus(1, $event)"
    @keydown.down.prevent="moveRadioFocus(1, $event)"
  >
    <div
      v-if="isInsideModal"
      class="pointer-events-none absolute left-0 top-0 size-full border transition-colors duration-300 group-hover:border-primary"
      :class="[
        isActive ? 'z-20 border-primary' : 'z-10',
        isInsideModal ? '' : 'rounded'
      ]"
    ></div>
    <ActiveVariantCheckmark v-if="isActive" :is-inside-modal="isInsideModal" />
    <ColornamePopover v-show="isInsideModal && option.title">{{
      option.title
    }}</ColornamePopover>

    <div
      class="relative grid place-items-center overflow-hidden"
      :class="[
        {
          'before:absolute before:left-0 before:top-1/2 before:z-10 before:block before:h-1 before:w-full before:origin-center before:rotate-45 before:bg-white after:absolute after:left-0 after:top-1/2 after:z-10 after:block after:h-1 after:w-full after:origin-center after:-rotate-45 after:bg-white':
            !option.inStock && !isSeeMore,

          'rounded-md border transition-all hover:border-gray-400':
            !isInsideModal && !isSeeMore,

          '!border-primary': isActive,
          'p-2 lg:p-4': isInsideModal
        },
        isSeeMore ? 'transition-opacity ease-in-out' : 'aspect-square w-full'
      ]"
    >
      <span
        v-if="
          isInsideModal && option.discontinued && !store.productIsDiscontinued
        "
        class="absolute left-px top-px z-1 max-w-[90%] truncate rounded-br bg-error-100 p-0.5 px-1.5 text-xs font-semibold text-error md:px-2 md:py-1"
        >{{ t('product.stock.text_discontinued') }}</span
      >
      <img
        class="w-full transition-all duration-300 ease-in-out"
        :src="itemImageUrl"
        loading="lazy"
        alt=""
        aria-hidden="true"
        :width="200"
        :height="200"
        :class="[
          isImgLoaded ? 'opacity-100' : 'opacity-0',
          {
            'opacity-60': !option.inStock && isImgLoaded,
            'group-hover:scale-105': isInsideModal && isImgLoaded
          }
        ]"
        @load="isImgLoaded = true"
      />

      <span
        v-if="isSeeMore"
        class="absolute left-0 top-0 flex aspect-square w-full items-center justify-center rounded-md bg-black/50 text-xl font-bold text-white transition-all ease-in-out group-[&.active]:text-primary"
        >{{ '+' + restOptionsAmount }}</span
      >
    </div>
    <div
      v-if="!isSeeMore"
      :class="{
        'absolute bottom-px left-px rounded': isInsideModal,
        'max-lg:truncate': !isInsideModal
      }"
      class="bg-white/90 px-1 py-px text-xs text-gray-500"
    >
      {{ optionName }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { useTranslations } from '@/shared/composables/useTranslations.ts';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options';

import ActiveVariantCheckmark from './ActiveVariantCheckmark.vue';
import ColornamePopover from './ColornamePopover.vue';
const store = useProductOptionsStore();

const { t } = useTranslations();

const isImgLoaded = ref(false);

// TODO: change out this loose type with an actual variant declaration.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TempLooseVariant = Record<string, any>;

const emit = defineEmits<{
  onClick: [
    payload: {
      option: TempLooseVariant;
      isSeeMore?: boolean;
      closeModal?: boolean;
    }
  ];
}>();

const props = defineProps<{
  option: TempLooseVariant;
  isSeeMore?: boolean;
  isActive?: boolean;
  isFocusable?: boolean;
  restOptionsAmount?: number;
  isInsideModal?: boolean;
}>();

const optionName = computed(() => props.option.title || props.option.colorCode);

const seeMoreLabel = computed(() =>
  t('product.options.text_see_more', { count: props.restOptionsAmount ?? 0 })
);

const select = () =>
  emit('onClick', { option: props.option, isSeeMore: props.isSeeMore });

const selectWithKeyboard = () =>
  emit('onClick', {
    option: props.option,
    isSeeMore: props.isSeeMore,
    closeModal: false
  });

const moveRadioFocus = (direction: number, event: KeyboardEvent) => {
  if (props.isSeeMore) return;

  const currentRadio = event.currentTarget as HTMLElement;
  const radioGroup = currentRadio.closest('[role="radiogroup"]');
  const radios = Array.from(
    radioGroup?.querySelectorAll<HTMLElement>('[role="radio"]') ?? []
  );
  const currentIndex = radios.indexOf(currentRadio);
  if (currentIndex < 0 || radios.length === 0) return;

  const nextIndex = (currentIndex + direction + radios.length) % radios.length;
  const nextRadio = radios[nextIndex];
  nextRadio?.focus();
  nextRadio?.dispatchEvent(new CustomEvent('gallery-radio-select'));
};

const itemImageUrl = computed(() => {
  const imageUrl = props.option.consolidatedImageUrl;
  if (!imageUrl || imageUrl.startsWith('http://')) return '';

  let url: URL;
  try {
    if (imageUrl.startsWith('https://')) {
      url = new URL(imageUrl);
    } else {
      // Otherwise, prepend the protocol
      url = new URL(window.location.protocol + imageUrl);
    }
  } catch {
    return '';
  }

  url.searchParams.set('height', '200');
  url.searchParams.set('width', '200');

  return url.toString();
});
</script>
